import { useFormik } from 'formik';
import { useEffect } from 'react';
import { TbHomePlus } from 'react-icons/tb';
import { useNavigate } from 'react-router-dom';
import { Alert, Button, Divider, Input, Select, message } from 'antd';
import { Routes } from '@/routes';
import { Flex, RetryAlert } from '@/components';
import { useThemeToken } from '@/hooks';
import { DormitoryValidation } from '@/validations';
import { useAvailableDormitories, useDormitoryPreRegisterForm } from '@/hooks/app';
import { useTranslation } from 'react-i18next';

export interface DormitoryPreRegisterFormProps {
  validEntrance: DormitoryValidEntranceResponse;
}

export default function DormitoryPreRegisterForm({
  validEntrance
}: DormitoryPreRegisterFormProps) {

  const navigate = useNavigate();
  const { colorPrimary } = useThemeToken();
  const { submit, submiting } = useDormitoryPreRegisterForm();
  const [t] = useTranslation(['common', 'dormitory']);

  const { dormitories, fetchDormitories, loading, error } = useAvailableDormitories();

  const formik = useFormik({
    validateOnMount: true,
    initialValues: {
      dormitoryId: null,
      description: ''
    },
    validationSchema: DormitoryValidation.getPreRegisterFormValidation(!validEntrance.preregisterWithoutChooseDormitory),
    onSubmit: async (values) => {
      try {
        await submit(values);
        message.success(t('common:messages.request-success-submit'));
        navigate(Routes.Dormitory);
      }
      catch (e) {
        // handle in http module
      }
    },
  });

  useEffect(() => {
    const withoutChoose = validEntrance?.preregisterWithoutChooseDormitory;
    // wihtoutChoose == null is for backward compatibility
    if (withoutChoose == null || withoutChoose === false) {
      fetchDormitories();
    }
  }, [validEntrance]);

  if (error) {
    return <Flex className="h-full" align="center"><RetryAlert noIcon content={error} action={fetchDormitories} /></Flex>;
  }

  return (
    <Flex gap="0.75rem" direction="column">
      <Alert
        type="info"
        className="!bg-stone-200 border border-stone-400"
        message={(
          <Flex className="p-1" direction="row">
            <span className="mb-auto">
              <TbHomePlus style={{ color: colorPrimary }} className="text-2xl" />
            </span>
            <span className="text-justify">
              {t('dormitory:messages.pre-register-info')}
            </span>
          </Flex>
        )} />
      <Divider className="!my-1 !mt-1.5" />
      {!validEntrance?.preregisterWithoutChooseDormitory &&
        <Select
          loading={loading}
          disabled={loading}
          className="w-full"
          placeholder={t('dormitory:messages.select-desired-dormitory')}
          onChange={value => formik.setFieldValue('dormitoryId', Number(value))}
          options={dormitories.map(dorm => ({ label: dorm.name, value: dorm.id }))}
        />}
      <Input.TextArea
        rows={7}
        name="description"
        disabled={loading}
        onChange={formik.handleChange}
        placeholder={t('common:labels.description')}
      />
      <Flex justify="flex-end" inlineFlex>
        <Button
          type="primary"
          disabled={loading || Object.keys(formik.errors).length > 0}
          loading={submiting}
          onClick={formik.submitForm}>
          {t('common:labels.submit-request')}
        </Button>
      </Flex>
    </Flex>
  );
}
